import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PhilosophySkeleton from './PhilosophySkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './Philosophy.scss';

const safeGetTranslation = (item, lang) => {
    if (!item) return { text: '', span: '', article: '' };
    return item[lang] || item.en || { text: '', span: '', article: '' };
};

const Philosophy = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { data: mainData, loading, error, refetch } = useCachedFetch('/data/locales/mainData.json');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const philosophyData = useMemo(() => {
        return mainData?.TopMainData?.PhilosophySection;
    }, [mainData]);

    const isDataValid = useMemo(() => {
        if (!philosophyData) return false;

        const requiredFields = [
            'contentTitle', 'contentItemOne', 'contentItemTwo', 
            'contentItemThree', 'contentItemFour', 'bgImage'
        ];

        return requiredFields.every(field => {
            const fieldData = philosophyData[field];
            if (!fieldData) return false;
            
            if (field !== 'bgImage') {
                return Object.values(fieldData).some(translation => 
                    translation?.text !== undefined
                );
            }
            
            return typeof fieldData === 'string' && fieldData.length > 0;
        });
    }, [philosophyData]);

    useEffect(() => {
        if (loading) return;

        if (error) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        if (isDataValid) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [loading, error, isDataValid]);

    if (isLoading) {
        return <PhilosophySkeleton />;
    }

    if (hasError || !philosophyData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className='careers__philosophy__section'>
            <div className='left__side__philosophy__section'>
                <AnimatedOnScroll animation="fade-up" delay={0}>
                <h3 className='left__side__philosophy__section__title'>
                    {safeGetTranslation(philosophyData.contentTitle, currentLang).text}
                    <span> {safeGetTranslation(philosophyData.contentTitle, currentLang).span}</span>
                </h3>
                </AnimatedOnScroll>
                <div className='left__side__philosophy__section__wrapper'>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                    <div className='left__side__philosophy__section__item'>
                        <h4 className='left__side__philosophy__section__item__subtitle subtitle__add__before__one'>
                            {safeGetTranslation(philosophyData.contentItemOne, currentLang).text}
                            <span> {safeGetTranslation(philosophyData.contentItemOne, currentLang).span}</span>
                        </h4>
                        <p className='left__side__philosophy__section__item__text'>
                            {safeGetTranslation(philosophyData.contentItemOne, currentLang).article}
                        </p>
                    </div>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={200}>
                    <div className='left__side__philosophy__section__item'>
                        <h4 className='left__side__philosophy__section__item__subtitle subtitle__add__before__two'>
                            {safeGetTranslation(philosophyData.contentItemTwo, currentLang).text}
                            <span> {safeGetTranslation(philosophyData.contentItemTwo, currentLang).span}</span>
                        </h4>
                        <p className='left__side__philosophy__section__item__text'>
                            {safeGetTranslation(philosophyData.contentItemTwo, currentLang).article}
                        </p>
                    </div>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={300}>
                    <div className='left__side__philosophy__section__item'>
                        <h4 className='left__side__philosophy__section__item__subtitle subtitle__add__before__three'>
                            {safeGetTranslation(philosophyData.contentItemThree, currentLang).text}
                            <span> {safeGetTranslation(philosophyData.contentItemThree, currentLang).span}</span>
                        </h4>
                        <p className='left__side__philosophy__section__item__text'>
                            {safeGetTranslation(philosophyData.contentItemThree, currentLang).article}
                        </p>
                    </div>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={400}>
                    <div className='left__side__philosophy__section__item unique'>
                        <h4 className='left__side__philosophy__section__item__subtitle subtitle__add__before__four'>
                            {safeGetTranslation(philosophyData.contentItemFour, currentLang).text}
                            <span> {safeGetTranslation(philosophyData.contentItemFour, currentLang).span}</span>
                        </h4>
                        <p className='left__side__philosophy__section__item__text'>
                            {safeGetTranslation(philosophyData.contentItemFour, currentLang).article}
                        </p>
                    </div>
                    </AnimatedOnScroll>
                </div>
            </div>
            <div className='right__side__philosophy__section'>
                <img 
                    className='philosophy__pic' 
                    src={`${IMAGES_BASE_URL}${philosophyData.bgImage}`}
                    alt='Team-Design-Studio'
                    onError={(e) => {
                        e.target.style.display = 'none';
                    }}
                    loading="lazy"
                />
            </div>
        </div>
    );
};

export default React.memo(Philosophy);